import { useMemo } from 'react'
import { useHederaWallet } from '@/hooks/wallets/useWallet'
import useSafeInfo from '@/hooks/useSafeInfo'
import type { SafeTransaction } from '@safe-global/safe-core-sdk-types'
import {
  dispatchHederaApproveHash,
  dispatchHederaSafeTxExecution,
  checkHederaSufficientApprovals,
} from '@/services/hedera/tx-dispatcher'
import { dispatchTxProposal } from '@/services/tx/tx-sender/dispatch'
import { getAndValidateSafeSDK } from '@/services/tx/tx-sender/sdk'
import { type HederaProvider } from '@hashgraph/hedera-wallet-connect'
import { useCurrentChain } from '../useChains'
import { createWeb3ReadOnly } from '../wallets/web3'
import { useAddressResolver } from '../useAddressResolver'

type HederaTxActions = {
  proposeTx: (safeTx: SafeTransaction, origin?: string) => Promise<string>
  approveHash: (safeTxHash: string, txId: string) => Promise<string>
  executeApprovedTx: (safeTx: SafeTransaction, txId: string) => Promise<string>
  checkApprovals: (
    safeTxHash: string,
    safeTx: SafeTransaction,
  ) => Promise<{
    approvalCount: number
    threshold: number
    isReady: boolean
    approvals: Record<string, { approved: boolean; data?: string }>
  }>
}

export const useHederaTxActions = (): HederaTxActions => {
  const { safe } = useSafeInfo()
  const wallet = useHederaWallet()
  const chain = useCurrentChain()
  const { ens } = useAddressResolver(safe.address.value, true)

  // Create a read-only provider from the chain info
  const readOnlyProvider = useMemo(() => {
    if (!chain) return undefined
    return createWeb3ReadOnly(chain)
  }, [chain])

  return useMemo<HederaTxActions>(() => {
    if (!wallet || !readOnlyProvider) {
      throw new Error('Hedera wallet not available')
    }

    const safeAddress = safe.address.value
    const { chainId } = safe

    /**
     * Propose the transaction to the tx-service without signatures
     * Uses the standard dispatchTxProposal (similar to proposers flow)
     */
    const proposeTx: HederaTxActions['proposeTx'] = async (safeTx, origin) => {
      const tx = await dispatchTxProposal({
        chainId,
        safeAddress,
        sender: wallet.address,
        safeTx, // SafeTransaction without signatures
        origin,
      })

      return tx.txId
    }

    /**
     * Execute approveHash onchain using HashPack
     * Uses hedera_signAndExecuteTransaction via WalletConnect
     */
    const approveHash: HederaTxActions['approveHash'] = async (safeTxHash, txId) => {
      return await dispatchHederaApproveHash(
        safeTxHash,
        ens || '',
        wallet.provider as HederaProvider,
        wallet.address, // This is the account ID or EVM address
        chainId,
        txId,
      )
    }

    /**
     * Execute the Safe transaction after having sufficient approvals
     * Uses hedera_signAndExecuteTransaction via WalletConnect
     */
    const executeApprovedTx: HederaTxActions['executeApprovedTx'] = async (safeTx, txId) => {
      const safeSDK = getAndValidateSafeSDK()
      const safeTxHash = await safeSDK.getTransactionHash(safeTx)

      const approvalStatus = await checkHederaSufficientApprovals(
        readOnlyProvider,
        safeAddress,
        safeTxHash,
        safeTx,
        safe.owners.map((o) => o.value),
      )

      if (!approvalStatus.isReady) {
        throw new Error(`Insufficient approvals: ${approvalStatus.approvalCount}/${approvalStatus.threshold}`)
      }

      return await dispatchHederaSafeTxExecution(
        safeTx,
        ens || '',
        wallet.provider as HederaProvider,
        wallet.address,
        chainId,
        approvalStatus.approvals,
        txId,
      )
    }

    /**
     * Check the approval status
     */
    const checkApprovals: HederaTxActions['checkApprovals'] = async (safeTxHash, safeTx) => {
      return await checkHederaSufficientApprovals(
        readOnlyProvider,
        safeAddress,
        safeTxHash,
        safeTx,
        safe.owners.map((o) => o.value),
      )
    }

    return { proposeTx, approveHash, executeApprovedTx, checkApprovals }
  }, [safe, wallet, readOnlyProvider, ens])
}
